import { useIdentity, useSignedInIdentity } from "../../../shared/useIdentity";
import React, { useEffect, useState } from "react";
import { LANGUAGE_NAMES } from "../../../shared/languages";
import { APP_DATA } from "../data/mockData";
import {
  approveCandidateReentry,
  decideCandidateAdmission,
  listCandidateReentryApprovals,
  listProctorCandidates,
  listProctorSessions,
  loadProctorRuntimeClock,
  loadProctorReadiness,
  recordProctorReadiness,
  type ProctorCandidate,
  type ProctorReadinessKind,
  type ProctorReentryCandidate,
  type ProctorRuntimeClock,
  type ProctorSession,
} from "../services/readiness";
import { listFactualIncidents, type FactualIncidentRecord } from "../services/incidents";
import {
  applyApprovedExamAccommodation,
  listApprovedExamAccommodations,
  recordSupervisedBreak,
  type ProctorExamAccommodation,
} from "../services/accommodations";
import type {
  Accommodation,
  Candidate,
  EvidenceRecord,
  Incident,
  Preferences,
  RouteDef,
  Session,
} from "../types/domain";
import { nowTime } from "../utils/browser";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Metric,
  PageHero,
  PlatformChip,
  Progress,
  Status,
  Toggle,
  initials,
  statusTone,
  type AppCallbacks,
} from "../components/proctor/WorkspaceLayouts";

export function CommandPage({ cb }: { cb: AppCallbacks }) {
  const identity = useSignedInIdentity();
  const bars = [68, 74, 81, 78, 86, 89, 84, 91];
  return (
    <>
      <PageHero
        kicker="Assessment Integrity Operations"
        title={`Welcome, ${identity.name}.`}
        description="Supervise assigned assessments, support candidates, preserve proportionate evidence and escalate observable facts for human review—without grading work or deciding misconduct."
      >
        <Button icon="monitor" variant="green" onClick={() => cb.navigate("rooms", "active-exam-room")}>
          Open live room
        </Button>
        <Button icon="plus" variant="orange" onClick={() => cb.openModal("record-incident")}>
          Record incident
        </Button>
        <Button icon="calendar" onClick={() => cb.navigate("sessions", "scheduled-sessions")}>
          View schedule
        </Button>
      </PageHero>
      <div className="integrity-banner">
        <span>
          <Icon name="shield" size={22} />
        </span>
        <div>
          <b>Signals support human attention; they are not findings.</b>
          <p>
            Review the event sequence, candidate context, accommodations and technical conditions before
            recording or escalating an incident.
          </p>
        </div>
        <Status>Human authority</Status>
      </div>
      <div className="metrics-grid">
        <Metric
          icon="video"
          value="3"
          label="Live assessment rooms"
          change="2 Class · 1 Academy"
          color="rgba(26,211,123,.15)"
          text="#73e4ad"
        />
        <Metric
          icon="users"
          value="82"
          label="Candidates in session"
          change="77 ready or active"
          color="rgba(47,214,244,.16)"
          text="#78e9fa"
        />
        <Metric
          icon="alert"
          value="12"
          label="Signals needing attention"
          change="5 human reviews"
          color="rgba(247,127,0,.18)"
          text="#ffba67"
        />
        <Metric
          icon="check"
          value="94%"
          label="Readiness completed"
          change="9 checks outstanding"
          color="rgba(139,92,246,.18)"
        />
      </div>
      <div className="dashboard-grid">
        <div>
          <Card
            title="Live room status"
            subtitle="Active candidate states across Academy Secure Room, Chatoner Class and Chatoner AI Labs."
            actions={
              <Button small icon="grid" onClick={() => cb.navigate("rooms", "candidate-grid")}>
                Candidate grid
              </Button>
            }
          >
            <div className="live-room-summary">
              <div className="live-room-main">
                <span className="live-pulse">
                  <i />
                </span>
                <div>
                  <small>LIVE · 01:12:43 elapsed</small>
                  <b>AI Product Builder — Secure Applied Examination</b>
                  <p>Academy Secure Room · APB-7Q4M · 38 candidates · Lead: Amina Mutesi</p>
                </div>
              </div>
              <div className="live-room-counts">
                {[
                  ["Normal", "29", "good"],
                  ["Review", "4", "warn"],
                  ["Technical", "3", "info"],
                  ["Approved break", "2", "violet"],
                ].map(([label, value, tone]) => (
                  <div key={label} className={`count-box ${tone}`}>
                    <b>{value}</b>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="candidate-mini-grid">
              {APP_DATA.candidates.slice(0, 8).map((candidate: Candidate) => (
                <button
                  key={candidate.id}
                  className={`candidate-mini ${statusTone(candidate.status)}`}
                  onClick={() => cb.openModal("candidate-detail", candidate)}
                >
                  <span className="avatar">
                    {initials(candidate.name)}
                    <i className={`presence ${candidate.online ? "online" : ""}`} />
                  </span>
                  <span>
                    <b>{candidate.name}</b>
                    <small>
                      {candidate.status} · {candidate.network}
                    </small>
                  </span>
                  <Icon name="more" size={14} />
                </button>
              ))}
            </div>
          </Card>
          <Card
            title="Operational pulse"
            subtitle="Illustrative eight-interval view of readiness, connectivity and response completion."
            actions={
              <Button small icon="download" onClick={() => cb.openModal("export")}>
                Export report
              </Button>
            }
          >
            <div className="chart-bars">
              {bars.map((v, i) => (
                <div className="chart-bar-wrap" key={i}>
                  <div className="chart-bar" data-value={`${v}%`} style={{ height: `${v}%` }} />
                  <div className="chart-label">T{i + 1}</div>
                </div>
              ))}
            </div>
            <div className="triple" style={{ marginTop: 13 }}>
              {[
                ["Candidate readiness", 94, "check"],
                ["Connection stability", 88, "pulse"],
                ["Actions documented", 91, "clipboard"],
              ].map(([label, value, icon]) => (
                <div className="meta-item" key={String(label)}>
                  <span>{label}</span>
                  <b style={{ fontSize: 16, margin: "7px 0" }}>{value}%</b>
                  <Progress value={Number(value)} green />
                  <small>
                    <Icon name={String(icon)} size={12} /> Live operational indicator
                  </small>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div>
          <Card
            title="Priority signals"
            subtitle="Triage by urgency, not by presumed wrongdoing."
            actions={
              <Button small onClick={() => cb.navigate("command", "priority-signals")}>
                All signals
              </Button>
            }
          >
            <div className="list">
              {APP_DATA.incidents.slice(0, 5).map((incident: Incident) => (
                <button
                  className="list-row"
                  key={incident.id}
                  onClick={() => cb.openModal("incident-detail", incident)}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                >
                  <span className="list-icon">
                    <Icon
                      name={
                        incident.category === "Connection"
                          ? "pulse"
                          : incident.category === "Accessibility"
                            ? "accessibility"
                            : "alert"
                      }
                      size={16}
                    />
                  </span>
                  <span className="list-copy">
                    <b>{incident.summary}</b>
                    <span>
                      {incident.candidate} · {incident.time} · {incident.source}
                    </span>
                  </span>
                  <Status>{incident.status}</Status>
                </button>
              ))}
            </div>
          </Card>
          <Card
            title="Next sessions"
            subtitle="Your confirmed assignments and preflight windows."
            actions={
              <Button small icon="calendar" onClick={() => cb.navigate("sessions", "proctor-calendar")}>
                Calendar
              </Button>
            }
          >
            <div className="list">
              {APP_DATA.sessions.slice(1, 5).map((session: Session) => (
                <button
                  className="list-row"
                  key={session.id}
                  onClick={() => cb.openModal("session-detail", session)}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                >
                  <span className="list-icon">
                    <Icon
                      name={
                        session.platform.includes("Labs")
                          ? "flask"
                          : session.platform.includes("Class")
                            ? "video"
                            : "shield"
                      }
                    />
                  </span>
                  <span className="list-copy">
                    <b>{session.title}</b>
                    <span>
                      {session.date} · {session.time} · {session.candidates} candidates
                    </span>
                  </span>
                  <Status>{session.status}</Status>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export function SessionsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [platform, setPlatform] = useState("All platforms");
  const [query, setQuery] = useState("");
  const shown = APP_DATA.sessions.filter(
    (s: Session) =>
      (platform === "All platforms" || s.platform === platform) &&
      `${s.title} ${s.program} ${s.code} ${s.lead}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <PageHero kicker="Sessions & Assignments" title={route.title} description={route.description}>
        <Button icon="calendar" variant="primary" onClick={() => cb.openModal("calendar")}>
          Open calendar
        </Button>
        <Button icon="users" onClick={() => cb.openModal("proctor-team")}>
          Proctor team
        </Button>
      </PageHero>
      <div className="metrics-grid">
        <Metric icon="calendar" value="8" label="Assigned sessions" change="3 this week" />
        <Metric
          icon="monitor"
          value="3"
          label="Platform types"
          change="Academy · Class · Labs"
          color="rgba(47,214,244,.16)"
          text="#7be9fa"
        />
        <Metric
          icon="users"
          value="229"
          label="Candidates covered"
          change="Across 8 sessions"
          color="rgba(26,211,123,.15)"
          text="#73e4ad"
        />
        <Metric
          icon="alert"
          value="6"
          label="Preflight actions"
          change="2 due today"
          color="rgba(247,127,0,.18)"
          text="#ffba67"
        />
      </div>
      <Card
        title="Assessment sessions"
        subtitle="Open a session to see assignment, candidates, rules, approved materials, accommodations and escalation contacts."
      >
        <div className="filters">
          <div className="input-wrap">
            <Icon name="search" size={14} />
            <input
              className="input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search session, program, code or proctor…"
            />
          </div>
          <select
            className="select compact-select"
            value={platform}
            onChange={e => setPlatform(e.target.value)}
          >
            <option>All platforms</option>
            <option>Academy Secure Room</option>
            <option>Chatoner Class</option>
            <option>Chatoner AI Labs</option>
          </select>
          <Button
            small
            icon="filter"
            onClick={() =>
              cb.toast("Filters applied", "The session list now uses the selected platform and search scope.")
            }
          >
            Apply
          </Button>
        </div>
        <div className="session-card-grid">
          {shown.map((session: Session) => (
            <article className="session-card" key={session.id}>
              <div className="session-card-top">
                <PlatformChip platform={session.platform} />
                <Status>{session.status}</Status>
              </div>
              <h3>{session.title}</h3>
              <p>
                {session.program} · {session.location}
              </p>
              <div className="session-meta-grid">
                <span>
                  <small>Date & time</small>
                  <b>
                    {session.date}
                    <br />
                    {session.time}
                  </b>
                </span>
                <span>
                  <small>Candidates</small>
                  <b>
                    {session.candidates} total
                    <br />
                    {session.ready} ready
                  </b>
                </span>
                <span>
                  <small>Your role</small>
                  <b>
                    {session.role}
                    <br />
                    {session.lead}
                  </b>
                </span>
                <span>
                  <small>Session code</small>
                  <b>
                    {session.code}
                    <br />
                    {session.risk}
                  </b>
                </span>
              </div>
              <Progress value={Math.round((session.ready / session.candidates) * 100)} green />
              <div className="session-card-actions">
                <Button small icon="eye" onClick={() => cb.openModal("session-detail", session)}>
                  Details
                </Button>
                {session.platform.includes("Class") && (
                  <Button
                    small
                    variant="primary"
                    icon="external"
                    onClick={() => cb.openClass(session, "join")}
                  >
                    Open Class
                  </Button>
                )}
                {session.platform.includes("Labs") && (
                  <Button
                    small
                    variant="primary"
                    icon="external"
                    onClick={() => cb.openLab(session, "proctor")}
                  >
                    Open Labs
                  </Button>
                )}
                {session.platform.includes("Academy") && (
                  <Button
                    small
                    variant="green"
                    icon="monitor"
                    onClick={() => cb.navigate("rooms", "active-exam-room")}
                  >
                    Open room
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      </Card>
    </>
  );
}

export function ReadinessPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [sessions, setSessions] = useState<ProctorSession[]>([]);
  const [sessionId, setSessionId] = useState("");
  const [candidates, setCandidates] = useState<ProctorCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All states");
  const [page, setPage] = useState(1);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [kind, setKind] = useState<ProctorReadinessKind>("identity");
  const [checkStatus, setCheckStatus] = useState("verified");
  const [method, setMethod] = useState("academy_record_match");
  const [observations, setObservations] = useState("");
  const [saving, setSaving] = useState(false);
  const [admissionDecision, setAdmissionDecision] = useState<"admitted" | "review_required" | "denied">(
    "admitted",
  );
  const [admissionReason, setAdmissionReason] = useState("");
  const [admissionSaving, setAdmissionSaving] = useState(false);
  const [reentryCandidates, setReentryCandidates] = useState<ProctorReentryCandidate[]>([]);
  const [reentryReason, setReentryReason] = useState("");
  const [reentrySaving, setReentrySaving] = useState(false);
  const pageSize = 12;
  const readinessOptions = {
    identity: {
      statuses: ["verified", "needs_review", "unable_to_verify"],
      methods: ["academy_record_match", "document_visual_check"],
    },
    device: {
      statuses: ["ready", "ready_with_support", "not_ready"],
      methods: ["guided_device_check", "platform_diagnostic"],
    },
    environment: {
      statuses: ["ready", "ready_with_support", "not_ready"],
      methods: ["guided_environment_check"],
    },
  } as const;
  const filtered = candidates.filter(
    candidate =>
      (status === "All states" || candidate.state === status) &&
      `${candidate.full_name} ${candidate.candidate_number} ${candidate.cohort_name} ${candidate.assessment_title}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safe = Math.min(page, pages);
  const visible = filtered.slice((safe - 1) * pageSize, safe * pageSize);
  useEffect(() => setPage(1), [query, status]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void listProctorSessions(controller.signal)
      .then(items => {
        setSessions(items);
        setSessionId(current => current || items[0]?.exam_session_id || "");
        setError("");
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof Error ? cause.message : "Assigned sessions are unavailable");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!sessionId) {
      setCandidates([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    void loadProctorReadiness(sessionId, controller.signal)
      .then(async result => {
        setCandidates(result.candidates);
        setSelectedCandidateId(current =>
          result.candidates.some(candidate => candidate.candidate_user_id === current)
            ? current
            : result.candidates[0]?.candidate_user_id || "",
        );
        setError("");
        if (route.id === "late-reentry-queue")
          setReentryCandidates(await listCandidateReentryApprovals(sessionId, controller.signal));
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof Error ? cause.message : "Candidate readiness is unavailable");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [route.id, sessionId]);
  const chooseKind = (next: ProctorReadinessKind) => {
    setKind(next);
    setCheckStatus(readinessOptions[next].statuses[0]);
    setMethod(readinessOptions[next].methods[0]);
  };
  const record = async () => {
    if (!sessionId || !selectedCandidateId || observations.trim().length < 5) return;
    setSaving(true);
    try {
      await recordProctorReadiness(sessionId, kind, {
        candidateUserId: selectedCandidateId,
        status: checkStatus,
        method,
        observations: observations.trim(),
      });
      const result = await loadProctorReadiness(sessionId);
      setCandidates(result.candidates);
      setObservations("");
      cb.toast(
        "Readiness evidence recorded",
        "The canonical Candidate Sitting now shows the latest check.",
        "good",
      );
    } catch (cause) {
      cb.toast(
        "Readiness evidence not recorded",
        cause instanceof Error ? cause.message : "The readiness command failed.",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };
  const recorded = (field: "identity_status" | "device_status" | "environment_status") =>
    candidates.filter(candidate => !["not_recorded", "booked"].includes(candidate[field])).length;
  const selectedCandidate = candidates.find(candidate => candidate.candidate_user_id === selectedCandidateId);
  const positiveReadiness = Boolean(
    selectedCandidate?.identity_status === "verified" &&
    ["ready", "ready_with_support"].includes(selectedCandidate.device_status) &&
    ["ready", "ready_with_support"].includes(selectedCandidate.environment_status),
  );
  const decideAdmission = async () => {
    if (!sessionId || !selectedCandidate || admissionReason.trim().length < 10) return;
    setAdmissionSaving(true);
    try {
      await decideCandidateAdmission(sessionId, {
        candidateUserId: selectedCandidate.candidate_user_id,
        decision: admissionDecision,
        reason: admissionReason.trim(),
        expectedVersion: selectedCandidate.admission_version,
      });
      const result = await loadProctorReadiness(sessionId);
      setCandidates(result.candidates);
      setAdmissionReason("");
      cb.toast(
        "Admission decision recorded",
        "The versioned human decision is visible in the canonical roster.",
        "good",
      );
    } catch (cause) {
      cb.toast(
        "Admission decision not recorded",
        cause instanceof Error ? cause.message : "The admission command failed.",
        "danger",
      );
    } finally {
      setAdmissionSaving(false);
    }
  };
  const approveReentry = async () => {
    const candidate = reentryCandidates.find(item => item.candidate_user_id === selectedCandidateId);
    if (!sessionId || !candidate || reentryReason.trim().length < 10) return;
    setReentrySaving(true);
    try {
      await approveCandidateReentry(sessionId, {
        candidateUserId: candidate.candidate_user_id,
        expectedEntrySequence: candidate.current_entry_sequence,
        reason: reentryReason.trim(),
      });
      setReentryCandidates(await listCandidateReentryApprovals(sessionId));
      setReentryReason("");
      cb.toast(
        "Re-entry approval recorded",
        "The immutable approval permits only the next entry sequence; no entry or academic attempt was created.",
        "good",
      );
    } catch (cause) {
      cb.toast(
        "Re-entry approval not recorded",
        cause instanceof Error ? cause.message : "The re-entry approval command failed.",
        "danger",
      );
    } finally {
      setReentrySaving(false);
    }
  };
  return (
    <>
      <PageHero kicker="Candidate Readiness" title={route.title} description={route.description}>
        <Button
          icon="clipboard"
          variant="primary"
          disabled={!candidates.length}
          onClick={() => setSelectedCandidateId(candidates[0]?.candidate_user_id || "")}
        >
          Record check
        </Button>
      </PageHero>
      <div className="readiness-strip">
        {[
          ["Booked candidates", candidates.length, "users"],
          ["Identity recorded", recorded("identity_status"), "user"],
          ["Device recorded", recorded("device_status"), "monitor"],
          ["Environment recorded", recorded("environment_status"), "eye"],
        ].map(([label, value, icon]) => (
          <div key={String(label)}>
            <span>
              <Icon name={String(icon)} size={16} />
            </span>
            <b>{value}</b>
            <small>{label}</small>
          </div>
        ))}
      </div>
      {candidates.length > 0 && (
        <Card
          title="Record factual readiness evidence"
          subtitle="This append-only check does not admit a candidate, start an attempt, or create an academic outcome."
        >
          <div className="filters">
            <select
              aria-label="Candidate"
              className="select"
              value={selectedCandidateId}
              onChange={event => setSelectedCandidateId(event.target.value)}
            >
              {candidates.map(candidate => (
                <option key={candidate.candidate_user_id} value={candidate.candidate_user_id}>
                  {candidate.candidate_number} · {candidate.full_name}
                </option>
              ))}
            </select>
            <select
              aria-label="Check kind"
              className="select"
              value={kind}
              onChange={event => chooseKind(event.target.value as ProctorReadinessKind)}
            >
              <option value="identity">Identity</option>
              <option value="device">Device</option>
              <option value="environment">Environment</option>
            </select>
            <select
              aria-label="Readiness status"
              className="select"
              value={checkStatus}
              onChange={event => setCheckStatus(event.target.value)}
            >
              {readinessOptions[kind].statuses.map(value => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              aria-label="Verification method"
              className="select"
              value={method}
              onChange={event => setMethod(event.target.value)}
            >
              {readinessOptions[kind].methods.map(value => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </div>
          <div className="filters">
            <input
              aria-label="Factual observations"
              className="input"
              maxLength={1000}
              value={observations}
              onChange={event => setObservations(event.target.value)}
              placeholder="Record observable facts (5–1000 characters)…"
            />
            <Button
              icon="check"
              variant="green"
              disabled={saving || observations.trim().length < 5}
              onClick={() => void record()}
            >
              {saving ? "Recording…" : "Record evidence"}
            </Button>
          </div>
        </Card>
      )}
      {route.id === "late-reentry-queue" && reentryCandidates.length > 0 && (
        <Card
          title="Approve the next candidate re-entry"
          subtitle="A current lead Proctor may approve one next sequence within the session policy. Approval alone creates no runtime entry, attempt or academic outcome."
        >
          <div className="filters">
            <select
              aria-label="Re-entry candidate"
              className="select"
              value={selectedCandidateId}
              onChange={event => setSelectedCandidateId(event.target.value)}
            >
              {reentryCandidates.map(candidate => (
                <option key={candidate.candidate_user_id} value={candidate.candidate_user_id}>
                  {candidate.candidate_number} · {candidate.full_name} · entry{" "}
                  {candidate.current_entry_sequence}
                </option>
              ))}
            </select>
            <input
              aria-label="Re-entry reason"
              className="input"
              maxLength={1000}
              value={reentryReason}
              onChange={event => setReentryReason(event.target.value)}
              placeholder="Factual re-entry reason (10–1000 characters)…"
            />
            <Button
              icon="history"
              variant="primary"
              disabled={
                reentrySaving ||
                reentryReason.trim().length < 10 ||
                !reentryCandidates.find(item => item.candidate_user_id === selectedCandidateId)?.can_approve
              }
              onClick={() => void approveReentry()}
            >
              {reentrySaving ? "Approving…" : "Approve next re-entry"}
            </Button>
          </div>
        </Card>
      )}
      {candidates.length > 0 && (
        <Card
          title="Record human admission decision"
          subtitle="This versioned entry decision requires current readiness and recent MFA. It does not grant runtime access, start an attempt or create an academic outcome. Late arrival and re-entry remain unavailable."
        >
          <div className="filters">
            <select
              aria-label="Admission candidate"
              className="select"
              value={selectedCandidateId}
              onChange={event => setSelectedCandidateId(event.target.value)}
            >
              {candidates.map(candidate => (
                <option key={candidate.candidate_user_id} value={candidate.candidate_user_id}>
                  {candidate.candidate_number} · {candidate.full_name}
                </option>
              ))}
            </select>
            <select
              aria-label="Admission decision"
              className="select"
              value={admissionDecision}
              onChange={event =>
                setAdmissionDecision(event.target.value as "admitted" | "review_required" | "denied")
              }
            >
              <option value="admitted" disabled={!positiveReadiness}>
                Admit
              </option>
              <option value="review_required">Require review</option>
              <option value="denied">Deny entry</option>
            </select>
            <input
              aria-label="Admission reason"
              className="input"
              maxLength={2000}
              value={admissionReason}
              onChange={event => setAdmissionReason(event.target.value)}
              placeholder="Factual human reason (10–2000 characters)…"
            />
            <Button
              icon="shield"
              variant="primary"
              disabled={
                admissionSaving ||
                admissionReason.trim().length < 10 ||
                (admissionDecision === "admitted" && !positiveReadiness)
              }
              onClick={() => void decideAdmission()}
            >
              {admissionSaving ? "Recording…" : "Record decision"}
            </Button>
          </div>
        </Card>
      )}
      <Card
        title="Assigned candidate roster"
        subtitle="Only exact booked Candidate Sittings within the current Proctor assignment are shown."
      >
        <div className="filters">
          <select
            aria-label="Assigned session"
            className="select"
            value={sessionId}
            onChange={event => setSessionId(event.target.value)}
          >
            {sessions.map(session => (
              <option key={session.exam_session_id} value={session.exam_session_id}>
                {session.assessment_title} · {session.cohort_name}
              </option>
            ))}
          </select>
          <div className="input-wrap">
            <Icon name="search" size={14} />
            <input
              className="input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search name, candidate number, cohort or assessment…"
            />
          </div>
          <select className="select compact-select" value={status} onChange={e => setStatus(e.target.value)}>
            {["All states", ...Array.from(new Set(candidates.map(candidate => candidate.state)))].map(
              value => (
                <option key={value}>{value}</option>
              ),
            )}
          </select>
          <Button small icon="shield" onClick={() => cb.openModal("scope-boundary")}>
            Visibility scope
          </Button>
        </div>
        {loading && (
          <EmptyState
            icon="history"
            title="Loading assigned readiness"
            text="Checking current authority and canonical Candidate Sittings…"
          />
        )}
        {!loading && error && <EmptyState icon="warning" title="Readiness unavailable" text={error} />}
        {!loading && !error && !candidates.length && (
          <EmptyState
            icon="users"
            title="No booked candidates"
            text="No canonical Candidate Sitting is visible in the selected assignment."
          />
        )}
        {!loading && !error && candidates.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Current state</th>
                  <th>Identity</th>
                  <th>Device</th>
                  <th>Environment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(candidate => (
                  <tr key={candidate.candidate_user_id}>
                    <td>
                      <span className="person">
                        <span className="avatar">
                          {initials(candidate.full_name)}
                          <i
                            className={`presence ${candidate.presence_state === "online" ? "online" : ""}`}
                          />
                        </span>
                        <span className="person-copy">
                          <b>{candidate.full_name}</b>
                          <span>
                            {candidate.candidate_number} · {candidate.cohort_name}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td>
                      <Status>{candidate.state}</Status>
                      <small className="cell-note">{candidate.presence_state}</small>
                    </td>
                    <td>
                      <Status>{candidate.identity_status}</Status>
                    </td>
                    <td>
                      <Status>{candidate.device_status}</Status>
                    </td>
                    <td>
                      <Status>{candidate.environment_status}</Status>
                    </td>
                    <td>
                      <Button
                        small
                        icon="clipboard"
                        onClick={() => setSelectedCandidateId(candidate.candidate_user_id)}
                      >
                        Record check
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination">
          <Button small disabled={safe <= 1} icon="arrow" onClick={() => setPage(v => Math.max(1, v - 1))}>
            Previous
          </Button>
          <span>
            {filtered.length} candidates · page {safe} of {pages}
          </span>
          <Button small disabled={safe >= pages} onClick={() => setPage(v => Math.min(pages, v + 1))}>
            Next
          </Button>
        </div>
      </Card>
    </>
  );
}
export function RoomPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All candidates");
  const [page, setPage] = useState(1);
  const [runtimeClock, setRuntimeClock] = useState<ProctorRuntimeClock>();
  const [runtimeClockError, setRuntimeClockError] = useState("");
  const [breakPlans, setBreakPlans] = useState<ProctorExamAccommodation[]>([]);
  const [breakPlansLoading, setBreakPlansLoading] = useState(false);
  const [breakPlansError, setBreakPlansError] = useState("");
  const [breakReasons, setBreakReasons] = useState<Record<string, string>>({});
  const [breakActing, setBreakActing] = useState("");
  const perPage = 12;
  const [message, setMessage] = useState("");
  const [roomMessages, setRoomMessages] = useState([
    {
      id: 1,
      author: "Amina Mutesi · Lead Proctor",
      time: "14:05",
      text: "Welcome. Keep this channel for procedural or technical support. Do not share assessment answers.",
    },
    {
      id: 2,
      author: "Sarah Achieng · Candidate",
      time: "14:17",
      text: "My connection recovered. The assessment timer and saved work are still visible.",
    },
    {
      id: 3,
      author: "Peter Ouma · Technical Support",
      time: "14:18",
      text: "Confirmed. The original timer and submission state were preserved.",
    },
    {
      id: 4,
      author: "Accessibility Office",
      time: "14:24",
      text: "The alternative keyboard-only panel is ready for the approved candidate.",
    },
  ]);
  useEffect(() => {
    if (route.id !== "breaks-reentry") return;
    const controller = new AbortController();
    setBreakPlansLoading(true);
    setBreakPlansError("");
    listProctorSessions(controller.signal)
      .then(async sessions => {
        const records = (
          await Promise.all(
            sessions.map(session =>
              listApprovedExamAccommodations(session.exam_session_id, controller.signal),
            ),
          )
        ).flat();
        setBreakPlans(
          records.filter(
            record =>
              record.accommodation_type === "supervised_breaks" && record.application_state === "applied",
          ),
        );
      })
      .catch(cause => {
        if (!controller.signal.aborted) {
          setBreakPlans([]);
          setBreakPlansError(
            cause instanceof Error ? cause.message : "Approved supervised breaks are unavailable.",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBreakPlansLoading(false);
      });
    return () => controller.abort();
  }, [route.id]);
  async function applySupervisedBreak(plan: ProctorExamAccommodation) {
    if (!plan.application_id) return;
    const reason = (breakReasons[plan.id] ?? "").trim();
    if (reason.length < 10) {
      setBreakPlansError("Record a factual break reason of at least 10 characters.");
      return;
    }
    const action = plan.open_break_id ? "end" : "start";
    setBreakActing(plan.id);
    setBreakPlansError("");
    try {
      const receipt = await recordSupervisedBreak(
        plan.exam_session_id,
        plan.candidate_user_id,
        plan.application_id,
        plan.break_count,
        action,
        plan.open_break_id,
        reason,
      );
      setBreakPlans(current =>
        current.map(item =>
          item.id === plan.id
            ? {
                ...item,
                break_count: action === "start" ? receipt.break_sequence : item.break_count,
                break_seconds_consumed: item.break_seconds_consumed + (receipt.credited_seconds ?? 0),
                open_break_id: action === "start" ? receipt.break_id : null,
                open_break_started_at: action === "start" ? receipt.recorded_at : null,
              }
            : item,
        ),
      );
      setBreakReasons(current => ({ ...current, [plan.id]: "" }));
      cb.toast(
        action === "start" ? "Supervised break started" : "Supervised break ended",
        "Immutable evidence was recorded without changing the shared room clock.",
        "good",
      );
    } catch (cause) {
      setBreakPlansError(cause instanceof Error ? cause.message : "Supervised-break action failed.");
    } finally {
      setBreakActing("");
    }
  }
  const filtered = APP_DATA.candidates.filter(
    (c: Candidate) =>
      (filter === "All candidates" || c.status === filter) &&
      `${c.name} ${c.candidateNo} ${c.status}`.toLowerCase().includes(query.toLowerCase()),
  );
  const pages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safe = Math.min(page, pages);
  const visible = filtered.slice((safe - 1) * perPage, safe * perPage);
  useEffect(() => setPage(1), [query, filter]);
  useEffect(() => {
    let active = true;
    let abort = new AbortController();
    const load = async () => {
      abort.abort();
      abort = new AbortController();
      try {
        const sessions = await listProctorSessions(abort.signal);
        if (!sessions[0]) throw new Error("No current assigned Exam Session is available.");
        const clock = await loadProctorRuntimeClock(sessions[0].exam_session_id, abort.signal);
        if (active) {
          setRuntimeClock(clock);
          setRuntimeClockError("");
        }
      } catch (cause) {
        if (active && !abort.signal.aborted) {
          setRuntimeClock(undefined);
          setRuntimeClockError(
            cause instanceof Error ? cause.message : "Server runtime clock is unavailable",
          );
        }
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    return () => {
      active = false;
      abort.abort();
      window.clearInterval(timer);
    };
  }, []);
  const formatClock = (seconds: number) => {
    const safe = Math.max(0, seconds);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const remainder = safe % 60;
    return [hours, minutes, remainder].map(value => String(value).padStart(2, "0")).join(":");
  };
  const send = () => {
    if (!message.trim()) return;
    setRoomMessages(v => [
      ...v,
      { id: Date.now(), author: "Amina Mutesi · Lead Proctor", time: nowTime(), text: message.trim() },
    ]);
    setMessage("");
    cb.toast("Secure message sent", "The message was added to the session communication record.", "good");
  };
  if (route.id === "secure-messages")
    return (
      <>
        <PageHero kicker="Session Communication" title={route.title} description={route.description}>
          <Button icon="send" variant="orange" onClick={() => cb.openModal("broadcast")}>
            Message all
          </Button>
          <Button icon="users" onClick={() => cb.openModal("proctor-team")}>
            Staff channel
          </Button>
        </PageHero>
        <div className="dashboard-grid">
          <Card
            title="Secure room conversation"
            subtitle="Messages are procedural, support-focused and linked to this session. They are not a place for grading or integrity decisions."
          >
            <div className="message-thread">
              {roomMessages.map(m => (
                <div className="message-bubble" key={m.id}>
                  <span className="avatar">{initials(m.author)}</span>
                  <div className="message-body">
                    <div className="message-head">
                      <b>{m.author}</b>
                      <span>{m.time}</span>
                    </div>
                    <p>{m.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="composer">
              <input
                className="input"
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") send();
                }}
                placeholder="Write a procedural or support message…"
              />
              <Button icon="send" variant="primary" onClick={send}>
                Send
              </Button>
            </div>
          </Card>
          <div>
            <Card
              title="Communication boundaries"
              subtitle="What the proctor may communicate during an assessment."
            >
              <div className="checklist">
                {[
                  [
                    "check",
                    "Allowed",
                    "Timing, navigation, technical support, approved rules and wellbeing support.",
                  ],
                  [
                    "check",
                    "Allowed",
                    "Clarify the procedure without interpreting the question or suggesting an answer.",
                  ],
                  [
                    "alert",
                    "Not allowed",
                    "Coach content, confirm an answer, grade, speculate about misconduct or expose another candidate.",
                  ],
                  [
                    "lock",
                    "Protected",
                    "Safeguarding, accessibility and wellbeing details go only to authorized channels.",
                  ],
                ].map(([icon, title, text]) => (
                  <div className="check-row" key={title + text}>
                    <span>
                      <Icon name={icon} />
                    </span>
                    <span className="check-row-copy">
                      <b>{title}</b>
                      <small>{text}</small>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Quick contacts">
              <div className="list">
                {[
                  ["Technical Support", "Peter Ouma", "pulse"],
                  ["Accessibility Office", "Lydia Atim", "accessibility"],
                  ["Integrity Reviewer", "Grace Namusoke", "shield"],
                  ["Safeguarding", "Restricted channel", "lock"],
                ].map(([name, detail, icon]) => (
                  <button
                    className="list-row"
                    key={name}
                    onClick={() => cb.openModal("candidate-message", { name, detail })}
                    style={{ width: "100%", textAlign: "left", color: "inherit" }}
                  >
                    <span className="list-icon">
                      <Icon name={icon} />
                    </span>
                    <span className="list-copy">
                      <b>{name}</b>
                      <span>{detail}</span>
                    </span>
                    <Icon name="message" size={14} />
                  </button>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </>
    );
  if (route.id === "session-controls")
    return (
      <>
        <PageHero
          kicker="Human-Controlled Session Operations"
          title={route.title}
          description={route.description}
        >
          <Button icon="clipboard" onClick={() => cb.openModal("policy-confirmation")}>
            View authority
          </Button>
        </PageHero>
        <div className="integrity-banner">
          <span>
            <Icon name="shield" />
          </span>
          <div>
            <b>Controls require purpose, authority and an audit note.</b>
            <p>
              No session control may be used to punish a candidate or replace the authorized assessment
              decision process.
            </p>
          </div>
          <Status>Lead proctor</Status>
        </div>
        <div className="action-grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 14 }}>
          {[
            ["Pause room timer", "Use for confirmed platform-wide disruption or emergency.", "clock", "warn"],
            ["Resume room timer", "Resume after the authorized pause reason is resolved.", "play", "good"],
            [
              "Apply approved extra time",
              "Only from an existing accommodation or assessor authorization.",
              "plus",
              "info",
            ],
            [
              "Lock new admissions",
              "Prevent additional admission after the authorized threshold.",
              "lock",
              "warn",
            ],
            [
              "Broadcast procedure",
              "Send one procedural message to all active candidates.",
              "message",
              "info",
            ],
            [
              "Emergency stop",
              "Pause and escalate where immediate safety or integrity continuity requires it.",
              "alert",
              "danger",
            ],
          ].map(([title, text, icon, tone]) => (
            <Card key={title} title={title} subtitle={text}>
              <Button
                variant={
                  tone === "danger"
                    ? "danger"
                    : tone === "good"
                      ? "green"
                      : tone === "warn"
                        ? "orange"
                        : "primary"
                }
                icon={icon}
                disabled={
                  !runtimeClock ||
                  (title === "Lock new admissions" && runtimeClock.admissions_locked) ||
                  (title === "Pause room timer" && runtimeClock.clock_state !== "running") ||
                  (title === "Resume room timer" && !runtimeClock.open_pause_id) ||
                  title === "Apply approved extra time"
                }
                onClick={() =>
                  tone === "danger"
                    ? cb.openModal("emergency")
                    : title.includes("Broadcast")
                      ? cb.openModal("broadcast")
                      : cb.openModal("secure-note", {
                          title,
                          text,
                          ...(title === "Lock new admissions"
                            ? {
                                control: "lock_new_admissions",
                                examSessionId: runtimeClock?.exam_session_id,
                                admissionsLocked: runtimeClock?.admissions_locked,
                              }
                            : title === "Pause room timer"
                              ? {
                                  control: "pause_timer",
                                  examSessionId: runtimeClock?.exam_session_id,
                                }
                              : title === "Resume room timer"
                                ? {
                                    control: "resume_timer",
                                    examSessionId: runtimeClock?.exam_session_id,
                                    pauseId: runtimeClock?.open_pause_id,
                                  }
                                : {}),
                        })
                }
              >
                {title === "Lock new admissions" && runtimeClock?.admissions_locked
                  ? "Admissions locked"
                  : title}
              </Button>
            </Card>
          ))}
        </div>
        <Card
          title="Recent control actions"
          subtitle="Every action retains the actor, purpose, timestamp, authorization and resulting state."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Authority</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [
                    "14:22",
                    "Candidate timer paused",
                    "Amina Mutesi",
                    "Approved accessibility plan",
                    "Resolved",
                  ],
                  [
                    "14:17",
                    "Submission state preserved",
                    "System + Peter Ouma",
                    "Technical continuity policy",
                    "Verified",
                  ],
                  ["14:11", "Approved medical break", "Amina Mutesi", "Accommodation plan", "Resolved"],
                  ["13:55", "New admission locked", "Amina Mutesi", "Session start policy", "Active"],
                ].map(row => (
                  <tr key={row[0] + row[1]}>
                    {row.map((v, i) => (
                      <td key={v}>{i === 4 ? <Status>{v}</Status> : v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </>
    );
  if (route.id === "breaks-reentry")
    return (
      <>
        <PageHero kicker="Approved Exceptions" title={route.title} description={route.description}>
          <Button icon="shield" variant="primary" disabled>
            Applied plans only
          </Button>
        </PageHero>
        {breakPlansLoading && <p role="status">Loading applied supervised-break plans…</p>}
        {breakPlansError && <p role="alert">{breakPlansError}</p>}
        {!breakPlansLoading && !breakPlansError && breakPlans.length === 0 && (
          <EmptyState
            icon="history"
            title="No applied supervised breaks"
            text="No applied supervised-break plan is available in your assigned sessions."
          />
        )}
        <div className="accommodation-grid">
          {breakPlans.map(plan => (
            <article className="record-card" key={plan.id}>
              <div className="record-head">
                <span className="platform-chip academy">
                  <Icon name="history" size={12} />
                  Controlled exception
                </span>
                <Status>{plan.open_break_id ? "Break open" : "Ready"}</Status>
              </div>
              <h3>{plan.candidate_name}</h3>
              <p>
                {plan.break_duration_minutes} minutes · {plan.max_breaks} approved breaks
              </p>
              <div className="record-meta">
                <span>
                  Session <b>{plan.assessment_title}</b>
                </span>
                <span>
                  Consumption{" "}
                  <b>
                    {plan.break_count} of {plan.max_breaks} started · {plan.break_seconds_consumed} seconds
                    credited
                  </b>
                </span>
                <span>
                  Instruction <b>{plan.functional_instruction}</b>
                </span>
                {plan.open_break_started_at && (
                  <span>
                    Started <b>{new Date(plan.open_break_started_at).toLocaleString()}</b>
                  </span>
                )}
              </div>
              <div className="record-actions">
                <Button
                  small
                  icon="eye"
                  onClick={() =>
                    cb.openModal("accommodation-detail", {
                      candidate: plan.candidate_name,
                      type: "supervised breaks",
                      session: plan.assessment_title,
                      owner: `Approved ${new Date(plan.approved_at).toLocaleDateString()}`,
                      status: "Approved",
                      instructions: plan.functional_instruction,
                      applicationState: "applied",
                    } as Accommodation)
                  }
                >
                  Open plan
                </Button>
                <label className="field">
                  <span>Factual break note</span>
                  <textarea
                    aria-label={`Break reason for ${plan.candidate_name}`}
                    value={breakReasons[plan.id] ?? ""}
                    onChange={event =>
                      setBreakReasons(current => ({ ...current, [plan.id]: event.target.value }))
                    }
                  />
                </label>
                <Button
                  small
                  icon="history"
                  variant="primary"
                  disabled={
                    breakActing === plan.id ||
                    (!plan.open_break_id && plan.break_count >= (plan.max_breaks ?? 0))
                  }
                  onClick={() => void applySupervisedBreak(plan)}
                >
                  {breakActing === plan.id
                    ? "Recording…"
                    : plan.open_break_id
                      ? "End supervised break"
                      : "Start supervised break"}
                </Button>
              </div>
            </article>
          ))}
        </div>
      </>
    );
  if (route.id === "emergency-procedures")
    return (
      <>
        <PageHero kicker="Safety & Continuity" title={route.title} description={route.description}>
          <Button icon="alert" variant="danger" onClick={() => cb.openModal("emergency")}>
            Open emergency action
          </Button>
        </PageHero>
        <div className="policy-grid">
          {[
            [
              "Platform-wide outage",
              "Pause affected timers, preserve work, notify technical lead and record continuity evidence.",
              "pulse",
            ],
            [
              "Candidate safety or wellbeing",
              "Stop monitoring that is not necessary, open the protected support path and hand over to safeguarding.",
              "shield",
            ],
            [
              "Accessibility barrier",
              "Pause where authorized, notify accessibility support and open the approved alternative delivery path.",
              "accessibility",
            ],
            [
              "Evacuation or environmental emergency",
              "Prioritize safety, record the room state and follow the session continuity decision from the authorized lead.",
              "alert",
            ],
            [
              "Evidence-service interruption",
              "Do not improvise covert capture. Record the service event and use the approved fallback log.",
              "database",
            ],
            [
              "Proctor capacity failure",
              "Call the assigned relief proctor and complete a factual, timestamped handover.",
              "users",
            ],
          ].map(([title, text, icon]) => (
            <Card key={title} title={title} subtitle={text}>
              <Button icon={icon} onClick={() => cb.openModal("emergency", { title, text })}>
                Open playbook
              </Button>
            </Card>
          ))}
        </div>
      </>
    );
  return (
    <>
      <PageHero kicker="Live Secure Assessment" title={route.title} description={route.description}>
        <Button icon="message" onClick={() => cb.openModal("broadcast")}>
          Message all
        </Button>
        <Button icon="plus" variant="orange" onClick={() => cb.openModal("record-incident")}>
          Record incident
        </Button>
        <Button icon="alert" variant="danger" onClick={() => cb.openModal("emergency")}>
          Emergency
        </Button>
      </PageHero>
      <div className="integrity-banner">
        <span>
          <Icon name="monitor" />
        </span>
        <div>
          <b>
            {runtimeClock
              ? `${runtimeClock.clock_state.toUpperCase()} EXAM · ${runtimeClock.entered_candidate_count} entered · ${formatClock(runtimeClock.elapsed_seconds)} elapsed`
              : "CANONICAL EXAM CLOCK LOADING"}
          </b>
          <p>
            {runtimeClock?.assessment_title ?? "Assigned secure assessment"} · server time authority ·
            recording limited to authorized event evidence.
          </p>
        </div>
        <Status>{runtimeClock?.clock_state ?? "Loading"}</Status>
      </div>
      {runtimeClockError ? (
        <p className="form-error" role="alert">
          {runtimeClockError}
        </p>
      ) : null}
      <div className="room-shell">
        <section className="exam-stage">
          <div className="exam-toolbar">
            <span className="live-pulse">
              <i />
            </span>
            <div>
              <b>Candidate grid</b>
              <small>{filtered.length} visible · human attention queue · large-group pagination</small>
            </div>
            <div className="timer">
              <small>{runtimeClock?.clock_state === "scheduled" ? "Duration" : "Remaining"}</small>
              <b>
                {runtimeClock
                  ? formatClock(
                      runtimeClock.clock_state === "scheduled"
                        ? runtimeClock.duration_seconds
                        : runtimeClock.remaining_seconds,
                    )
                  : "--:--:--"}
              </b>
            </div>
          </div>
          <div className="filters" style={{ padding: "10px 12px 0" }}>
            <div className="input-wrap">
              <Icon name="search" size={14} />
              <input
                className="input"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search candidate or number…"
              />
            </div>
            <select
              className="select compact-select"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              {[
                "All candidates",
                ...Array.from(new Set(APP_DATA.candidates.map((c: Candidate) => c.status))),
              ].map(v => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </div>
          <div className="candidate-grid">
            {visible.map((c: Candidate) => (
              <button
                key={c.id}
                className={`candidate-tile ${statusTone(c.status)}`}
                onClick={() => cb.openModal("candidate-detail", c)}
              >
                <div className="candidate-video">
                  <span className="avatar">
                    {initials(c.name)}
                    <i className={`presence ${c.online ? "online" : ""}`} />
                  </span>
                  <div className="video-status">
                    <span>
                      <Icon name={c.camera ? "camera" : "alert"} size={9} />
                      {c.camera ? "Camera" : "Check"}
                    </span>
                    <span>
                      <Icon name={c.microphone ? "mic" : "alert"} size={9} />
                      {c.network}
                    </span>
                  </div>
                </div>
                <div className="candidate-tile-copy">
                  <b>{c.name}</b>
                  <small>
                    {c.candidateNo} · {c.timeRemaining}
                  </small>
                </div>
                <span className="tile-status">
                  <Status>{c.status}</Status>
                </span>
              </button>
            ))}
          </div>
          <div className="pagination" style={{ padding: "0 12px 12px" }}>
            <Button small disabled={safe <= 1} onClick={() => setPage(v => Math.max(1, v - 1))}>
              Previous
            </Button>
            <span>
              Page {safe} of {pages}
            </span>
            <Button small disabled={safe >= pages} onClick={() => setPage(v => Math.min(pages, v + 1))}>
              Next
            </Button>
          </div>
        </section>
        <aside className="room-side">
          <Card title="Room status">
            <div className="live-room-counts" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 0 }}>
              {[
                ["Normal", 29, "good"],
                ["Review", 4, "warn"],
                ["Technical", 3, "info"],
                ["Approved break", 2, "violet"],
              ].map(([l, v, t]) => (
                <div key={String(l)} className={`count-box ${t}`}>
                  <b>{v}</b>
                  <span>{l}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card
            title="Priority timeline"
            subtitle="Events request attention; they do not establish an outcome."
          >
            <div className="timeline">
              {APP_DATA.incidents.slice(0, 5).map((i: Incident) => (
                <button
                  className="timeline-row"
                  key={i.id}
                  onClick={() => cb.openModal("incident-detail", i)}
                  style={{
                    borderTop: 0,
                    borderLeft: 0,
                    borderRight: 0,
                    background: "transparent",
                    color: "inherit",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  <span className="timeline-dot" />
                  <span className="timeline-copy">
                    <b>
                      {i.time} · {i.candidate}
                    </b>
                    <span>{i.summary}</span>
                  </span>
                </button>
              ))}
            </div>
          </Card>
          <Card title="Room controls">
            <div className="action-grid">
              <Button small icon="message" onClick={() => cb.openModal("broadcast")}>
                Broadcast
              </Button>
              <Button small icon="clock" onClick={() => cb.openModal("break-action")}>
                Break
              </Button>
              <Button small icon="history" onClick={() => cb.openModal("handover")}>
                Handover
              </Button>
              <Button small icon="sliders" onClick={() => cb.navigate("rooms", "session-controls")}>
                Controls
              </Button>
            </div>
          </Card>
        </aside>
      </div>
    </>
  );
}

export function IncidentsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [query, setQuery] = useState("");
  const [state, setState] = useState("All states");
  const [sessions, setSessions] = useState<ProctorSession[]>([]);
  const [sessionId, setSessionId] = useState("");
  const [candidates, setCandidates] = useState<ProctorCandidate[]>([]);
  const [incidents, setIncidents] = useState<FactualIncidentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void listProctorSessions(controller.signal)
      .then(items => {
        setSessions(items);
        setSessionId(current =>
          items.some(item => item.exam_session_id === current) ? current : items[0]?.exam_session_id || "",
        );
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setSessions([]);
        setSessionId("");
        setError(cause instanceof Error ? cause.message : "Assigned sessions are unavailable.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!sessionId) {
      setCandidates([]);
      setIncidents([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void Promise.all([
      listProctorCandidates(sessionId, controller.signal),
      listFactualIncidents(sessionId, controller.signal),
    ])
      .then(([candidateRows, incidentRows]) => {
        setCandidates(candidateRows);
        setIncidents(incidentRows);
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setCandidates([]);
        setIncidents([]);
        setError(cause instanceof Error ? cause.message : "Incident records are unavailable.");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [sessionId, refresh]);
  useEffect(() => {
    const reload = (event: Event) => {
      const changedSession = (event as CustomEvent<{ examSessionId?: string }>).detail?.examSessionId;
      if (changedSession === sessionId) setRefresh(value => value + 1);
    };
    window.addEventListener("academy:proctor-incident-recorded", reload);
    return () => window.removeEventListener("academy:proctor-incident-recorded", reload);
  }, [sessionId]);
  const candidateNames = new Map(candidates.map(item => [item.candidate_user_id, item.full_name]));
  const canonicalRecords: Incident[] = incidents.map(item => {
    const [headline, ...detail] = item.observable_facts.split("\n\n");
    return {
      id: item.id,
      candidate: item.candidate_user_id
        ? candidateNames.get(item.candidate_user_id) || "Assigned candidate"
        : "Room-wide",
      category: item.category.replace("_", " "),
      severity: item.severity,
      status: "Recorded",
      time: new Date(item.occurred_at).toLocaleString(),
      source: "Human observation",
      summary: headline,
      facts: detail.join("\n\n") || item.observable_facts,
      action: item.immediate_action || "No immediate action recorded.",
      evidence: [],
      owner: "Assigned Proctor",
    };
  });
  const records = canonicalRecords.filter(
    (i: Incident) =>
      (state === "All states" || i.status === state) &&
      `${i.id} ${i.candidate} ${i.category} ${i.summary}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <PageHero kicker="Factual Records & Human Review" title={route.title} description={route.description}>
        <Button icon="plus" variant="orange" onClick={() => cb.openModal("record-incident")}>
          Record incident
        </Button>
        <Button icon="download" onClick={() => cb.openModal("export")}>
          Export queue
        </Button>
      </PageHero>
      <div className="integrity-banner">
        <span>
          <Icon name="alert" />
        </span>
        <div>
          <b>Record observable facts, not conclusions.</b>
          <p>
            The proctor records what happened, what source produced the signal, what support was given and
            what evidence exists. Authorized reviewers—not the proctor—decide any academic-integrity outcome.
          </p>
        </div>
        <Status>Human review</Status>
      </div>
      <div className="metrics-grid">
        <Metric
          icon="inbox"
          value={String(incidents.length)}
          label="Canonical factual records"
          change="No findings implied"
        />
        <Metric
          icon="monitor"
          value={String(incidents.filter(item => item.category === "technical").length)}
          label="Technical events"
          change="Separated from integrity"
          color="rgba(47,214,244,.16)"
          text="#75e9f9"
        />
        <Metric
          icon="accessibility"
          value={String(
            incidents.filter(item => ["accessibility", "wellbeing"].includes(item.category)).length,
          )}
          label="Access & wellbeing"
          change="Protected routing"
          color="rgba(26,211,123,.14)"
          text="#72e5ad"
        />
        <Metric
          icon="history"
          value={String(incidents.filter(item => item.severity === "urgent").length)}
          label="Urgent factual records"
          change="Human review required"
          color="rgba(247,127,0,.16)"
          text="#ffb65f"
        />
      </div>
      <Card
        title="Incident records"
        subtitle="Canonical assigned-session facts. Review, escalation and closure remain separate governed authority."
      >
        <div className="filters">
          <select
            aria-label="Incident session"
            className="select compact-select"
            value={sessionId}
            onChange={event => setSessionId(event.target.value)}
            disabled={loading}
          >
            {sessions.length === 0 && <option value="">No assigned session</option>}
            {sessions.map(item => (
              <option key={item.exam_session_id} value={item.exam_session_id}>
                {item.assessment_title} · {item.cohort_name}
              </option>
            ))}
          </select>
          <div className="input-wrap">
            <Icon name="search" size={14} />
            <input
              className="input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search candidate, category, record or observable fact…"
            />
          </div>
          <select className="select compact-select" value={state} onChange={e => setState(e.target.value)}>
            {["All states", ...Array.from(new Set(canonicalRecords.map((i: Incident) => i.status)))].map(
              v => (
                <option key={v}>{v}</option>
              ),
            )}
          </select>
        </div>
        <div className="incident-grid">
          {records.map((i: Incident) => (
            <article className="record-card" key={i.id}>
              <div className="record-head">
                <span className="platform-chip academy">
                  <Icon name="alert" size={12} />
                  {i.id}
                </span>
                <Status>{i.status}</Status>
              </div>
              <h3>{i.summary}</h3>
              <p>
                {i.candidate} · {i.category} · {i.time}
              </p>
              <div className="record-meta">
                <span>
                  Source <b>{i.source}</b>
                </span>
                <span>
                  Severity <b>{i.severity}</b>
                </span>
                <span>
                  Owner <b>{i.owner}</b>
                </span>
                <span>
                  Evidence <b>{i.evidence.length ? i.evidence.join(", ") : "No linked evidence"}</b>
                </span>
              </div>
              <div className="record-actions">
                <Button small icon="eye" onClick={() => cb.openModal("incident-detail", i)}>
                  Review facts
                </Button>
                <Button
                  small
                  icon="arrow"
                  variant="primary"
                  onClick={() => cb.openModal("secure-note", { title: "Escalate incident", incident: i })}
                >
                  Escalate
                </Button>
              </div>
            </article>
          ))}
        </div>
        {!loading && !records.length && (
          <EmptyState
            icon="search"
            title={error ? "Incident records unavailable" : "No canonical records match"}
            text={error || "Change the assigned session, incident search or state filter."}
          />
        )}
      </Card>
    </>
  );
}

export function EvidencePage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [query, setQuery] = useState("");
  const records = APP_DATA.evidence.filter((e: EvidenceRecord) =>
    `${e.id} ${e.type} ${e.candidate} ${e.source} ${e.status}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <PageHero kicker="Purpose-Limited Evidence" title={route.title} description={route.description}>
        <Button icon="download" variant="primary" onClick={() => cb.openModal("evidence-export")}>
          Prepare manifest
        </Button>
        <Button icon="shield" onClick={() => cb.navigate("evidence", "retention-access")}>
          Access policy
        </Button>
      </PageHero>
      <div className="metrics-grid">
        <Metric icon="database" value="8" label="Linked evidence records" change="All have provenance" />
        <Metric
          icon="lock"
          value="100%"
          label="Hash and custody coverage"
          change="Verified"
          color="rgba(26,211,123,.15)"
          text="#72e5ad"
        />
        <Metric
          icon="clock"
          value="90–180d"
          label="Default retention"
          change="Policy-dependent"
          color="rgba(47,214,244,.14)"
          text="#75e8fa"
        />
        <Metric
          icon="download"
          value="2"
          label="Pending export requests"
          change="Permission check required"
          color="rgba(247,127,0,.16)"
          text="#ffb55e"
        />
      </div>
      <Card
        title="Evidence inventory"
        subtitle="Only evidence authorized by the published assessment policy is listed. Every access and export is attributable."
      >
        <div className="filters">
          <div className="input-wrap">
            <Icon name="search" size={14} />
            <input
              className="input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search evidence ID, type, candidate, source or status…"
            />
          </div>
          <Button
            small
            icon="filter"
            onClick={() =>
              cb.toast(
                "Evidence scope checked",
                "The list remains limited to assigned sessions and authorized evidence classes.",
                "good",
              )
            }
          >
            Check scope
          </Button>
        </div>
        <div className="evidence-grid">
          {records.map((e: EvidenceRecord) => (
            <article className="record-card" key={e.id}>
              <div className="record-head">
                <span className="platform-chip academy">
                  <Icon name="database" size={12} />
                  {e.id}
                </span>
                <Status>{e.status}</Status>
              </div>
              <h3>{e.type}</h3>
              <p>
                {e.candidate} · {e.captured}
              </p>
              <div className="record-meta">
                <span>
                  Source <b>{e.source}</b>
                </span>
                <span>
                  Custodian <b>{e.custodian}</b>
                </span>
                <span>
                  Access <b>{e.access}</b>
                </span>
                <span>
                  Retention <b>{e.retention}</b>
                </span>
                <span>
                  Integrity hash <b>{e.hash}</b>
                </span>
              </div>
              <div className="record-actions">
                <Button small icon="eye" onClick={() => cb.openModal("evidence-detail", e)}>
                  Open record
                </Button>
                <Button small icon="link" onClick={() => cb.navigate("evidence", "chain-of-custody")}>
                  Custody
                </Button>
              </div>
            </article>
          ))}
        </div>
      </Card>
    </>
  );
}

export function AccommodationsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applicationReasons, setApplicationReasons] = useState<Record<string, string>>({});
  const [applying, setApplying] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    listProctorSessions(controller.signal)
      .then(async sessions => {
        const records = (
          await Promise.all(
            sessions.map(session =>
              listApprovedExamAccommodations(session.exam_session_id, controller.signal),
            ),
          )
        ).flat();
        setAccommodations(
          records.map(record => ({
            candidate: record.candidate_name,
            type: record.accommodation_type.replace(/_/g, " "),
            session: record.assessment_title,
            owner: `Approved ${new Date(record.approved_at).toLocaleDateString()}`,
            status: "Approved",
            instructions: record.functional_instruction,
            accommodationId: record.id,
            examSessionId: record.exam_session_id,
            accommodationVersion: record.version,
            applicationState: record.application_state,
            appliedAt: record.applied_at,
            supportCoverageEndsAt: record.support_coverage_ends_at,
            candidateUserId: record.candidate_user_id,
            applicationId: record.application_id,
            breakDurationMinutes: record.break_duration_minutes,
            maxBreaks: record.max_breaks,
            breakCount: record.break_count,
            breakSecondsConsumed: record.break_seconds_consumed,
            openBreakId: record.open_break_id,
            openBreakStartedAt: record.open_break_started_at,
          })),
        );
      })
      .catch(cause => {
        if (!controller.signal.aborted) {
          setAccommodations([]);
          setError(cause instanceof Error ? cause.message : "Approved accommodations are unavailable.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  async function apply(accommodation: Accommodation) {
    if (
      !accommodation.accommodationId ||
      !accommodation.examSessionId ||
      accommodation.accommodationVersion !== 2
    )
      return;
    const reason = (applicationReasons[accommodation.accommodationId] ?? "").trim();
    if (reason.length < 10) {
      setError("Record an application reason of at least 10 characters.");
      return;
    }
    setApplying(accommodation.accommodationId);
    setError("");
    try {
      const receipt = await applyApprovedExamAccommodation(
        accommodation.examSessionId,
        accommodation.accommodationId,
        2,
        reason,
      );
      setAccommodations(current =>
        current.map(item =>
          item.accommodationId === accommodation.accommodationId
            ? {
                ...item,
                applicationState: "applied",
                appliedAt: receipt.applied_at,
                supportCoverageEndsAt: receipt.support_coverage_ends_at,
              }
            : item,
        ),
      );
      cb.toast(
        "Accommodation applied",
        "The exact approved instruction now has immutable delivery evidence.",
        "good",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Accommodation application failed.");
    } finally {
      setApplying("");
    }
  }
  return (
    <>
      <PageHero
        kicker="Equal Access & Approved Adjustments"
        title={route.title}
        description={route.description}
      >
        <Button icon="accessibility" variant="primary" onClick={() => cb.openModal("scope-boundary")}>
          View access boundary
        </Button>
        <Button
          icon="message"
          onClick={() =>
            cb.openModal("candidate-message", {
              name: "Accessibility Office",
              detail: "Restricted support channel",
            })
          }
        >
          Contact accessibility
        </Button>
      </PageHero>
      <div className="integrity-banner">
        <span>
          <Icon name="accessibility" />
        </span>
        <div>
          <b>Proctors apply approved accommodations; they do not diagnose, approve or remove them.</b>
          <p>
            Only the minimum session instruction required for delivery is shown. Underlying medical or
            protected information remains outside the proctor workspace.
          </p>
        </div>
        <Status>Need-to-know</Status>
      </div>
      {loading && <p role="status">Loading approved accommodations…</p>}
      {error && <p role="alert">{error}</p>}
      {!loading && !error && accommodations.length === 0 && (
        <EmptyState
          icon="accessibility"
          title="No approved accommodations"
          text="No minimum-necessary delivery instructions are active for your assigned sessions."
        />
      )}
      <div className="accommodation-grid">
        {accommodations.map((a: Accommodation) => (
          <article className="record-card" key={a.candidate + a.type + a.session}>
            <div className="record-head">
              <span className="platform-chip class">
                <Icon name="accessibility" size={12} />
                Approved plan
              </span>
              <Status>{a.status}</Status>
            </div>
            <h3>{a.candidate}</h3>
            <p>{a.type}</p>
            {a.applicationState === "applied" && (
              <p>
                <Status>Applied</Status>
                {a.supportCoverageEndsAt && (
                  <> · Proctor coverage through {new Date(a.supportCoverageEndsAt).toLocaleString()}</>
                )}
              </p>
            )}
            <div className="record-meta">
              <span>
                Session <b>{a.session}</b>
              </span>
              <span>
                Authority <b>{a.owner}</b>
              </span>
              <span>
                Proctor instruction <b>{a.instructions}</b>
              </span>
            </div>
            <div className="record-actions">
              <Button small icon="eye" onClick={() => cb.openModal("accommodation-detail", a)}>
                Open instruction
              </Button>
              {a.applicationState !== "applied" && (
                <>
                  <label className="field">
                    <span>Application reason</span>
                    <textarea
                      aria-label={`Application reason for ${a.candidate}`}
                      value={applicationReasons[a.accommodationId ?? ""] ?? ""}
                      onChange={event =>
                        a.accommodationId &&
                        setApplicationReasons(current => ({
                          ...current,
                          [a.accommodationId as string]: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <Button
                    small
                    variant="primary"
                    icon="history"
                    disabled={applying === a.accommodationId}
                    onClick={() => void apply(a)}
                  >
                    {applying === a.accommodationId ? "Applying…" : "Apply approved instruction"}
                  </Button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
      <div className="boundary-grid">
        <div className="boundary-card allow">
          <h3>
            <Icon name="check" />
            Proctor can
          </h3>
          <ul>
            {[
              "See the approved session instruction and responsible office.",
              "Verify that the required assistive technology or caption setting is ready.",
              "Apply the pre-authorized timing, break or re-entry step.",
              "Pause and escalate when an accessibility barrier prevents fair participation.",
            ].map(v => (
              <li key={v}>
                <Icon name="check" size={13} />
                {v}
              </li>
            ))}
          </ul>
        </div>
        <div className="boundary-card deny">
          <h3>
            <Icon name="lock" />
            Proctor cannot
          </h3>
          <ul>
            {[
              "View diagnoses or protected records not needed for delivery.",
              "Approve, deny, reduce or reinterpret an accommodation.",
              "Treat assistive technology as suspicious behavior.",
              "Penalize a candidate for an accessibility or platform failure.",
            ].map(v => (
              <li key={v}>
                <Icon name="close" size={13} />
                {v}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
export function ClassPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const classSessions = cb.classSessions;
  const [selectedId, setSelectedId] = useState(classSessions[0]?.id || "");
  const selected = classSessions.find((s: Session) => s.id === selectedId) || classSessions[0];
  const [joinValue, setJoinValue] = useState(selected?.code || "");
  useEffect(() => {
    if (classSessions.length && !classSessions.some(session => session.id === selectedId)) {
      setSelectedId(classSessions[0].id);
    }
  }, [classSessions, selectedId]);
  useEffect(() => setJoinValue(selected?.code || ""), [selected?.code]);
  return (
    <>
      <PageHero
        kicker="Independent Live Assessment Platform"
        title={route.title}
        description={route.description}
      >
        <Button
          icon="external"
          variant="primary"
          disabled={!selected}
          onClick={() => selected && cb.openClass(selected, "proctor")}
        >
          Open Proctor Room
        </Button>
        <Button icon="calendar" onClick={() => cb.openModal("calendar")}>
          Class schedule
        </Button>
      </PageHero>
      <div className="platform-hero">
        <div className="platform-hero-top">
          <span className="platform-logo">
            <Icon name="video" size={24} />
          </span>
          <div>
            <h3>Chatoner Class · class.chatoner.com</h3>
            <p>
              Class independently owns live media, waiting rooms, admission, captions, interpretation,
              recording and session security. Academy will require a short-lived, role-scoped handoff and
              verified attendance and evidence summaries before this connection can operate.
            </p>
          </div>
        </div>
        <div className="connection-line">
          <i />
          Current assignment and one-time destination are verified at launch
        </div>
      </div>
      {cb.classSessionsLoading && <p role="status">Loading assigned Class sessions…</p>}
      {cb.classSessionsError && <p role="alert">{cb.classSessionsError}</p>}
      {!cb.classSessionsLoading && !cb.classSessionsError && !classSessions.length && (
        <p>No current assigned Chatoner Class exam sessions are available.</p>
      )}
      <div className="handoff-panel">
        <Card
          title="Session-specific Class access"
          subtitle="Preview session details. A verified assignment and one-time handoff are required for access."
        >
          <div className="field">
            <span>Assigned live assessment</span>
            <select className="select" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              {classSessions.map((s: Session) => (
                <option key={s.id} value={s.id}>
                  {s.title} · {s.date}
                </option>
              ))}
            </select>
          </div>
          <div className="handoff-code">
            <small>Preview class code</small>
            <b>{selected?.code}</b>
            <p>
              {selected?.title}
              <br />
              {selected?.date} · {selected?.time} · {selected?.candidates} candidates · {selected?.role}
            </p>
            <div className="record-actions">
              <Button small icon="copy" disabled>
                Copy code
              </Button>
              <Button small icon="copy" disabled>
                Copy link
              </Button>
            </div>
          </div>
          <div className="field">
            <span>Enter a Class code or complete invitation link</span>
            <input
              className="input"
              value={joinValue}
              onChange={e => setJoinValue(e.target.value)}
              placeholder="Class code or invitation link"
            />
          </div>
          <div className="record-actions">
            <Button
              icon="external"
              variant="primary"
              onClick={() =>
                selected &&
                cb.openClass(
                  { ...selected, code: joinValue.includes("http") ? selected.code : joinValue } as Session,
                  "proctor",
                )
              }
            >
              Continue to Chatoner Class
            </Button>
            <Button icon="play" onClick={() => selected && cb.openClass(selected, "rehearsal")}>
              Open rehearsal
            </Button>
          </div>
        </Card>
        <div>
          <Card title="Context required by Class">
            <div className="checklist">
              {[
                ["user", "Current proctor", "Verified identity and current session assignment required"],
                [
                  "shield",
                  "Assessment scope",
                  `${selected?.id} · ${selected?.program} · ${selected?.candidates} candidates`,
                ],
                [
                  "globe",
                  "Language & access",
                  "Primary language, secondary translation and captions preference",
                ],
                ["link", "Safe return", "Allow-listed Academy Proctor destination after the session"],
              ].map(([icon, title, text]) => (
                <div className="check-row" key={title}>
                  <span>
                    <Icon name={icon} />
                  </span>
                  <span className="check-row-copy">
                    <b>{title}</b>
                    <small>{text}</small>
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Class product boundary">
            <div className="boundary-grid" style={{ gridTemplateColumns: "1fr" }}>
              <div className="boundary-card allow">
                <h3>
                  <Icon name="check" />
                  Class owns
                </h3>
                <ul>
                  {[
                    "Live media, waiting room and participant admission.",
                    "Captions, interpretation, recording and transcripts.",
                    "In-session security, presence and technical continuity.",
                    "Room-scoped evidence generated under the published policy.",
                  ].map(v => (
                    <li key={v}>
                      <Icon name="check" size={12} />
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Card
        title="Assigned Class examination sessions"
        subtitle="Oral examinations, portfolio defences, interviews and live panel assessments."
      >
        <div className="session-card-grid">
          {classSessions.map((s: Session) => (
            <article className="session-card" key={s.id}>
              <div className="session-card-top">
                <PlatformChip platform={s.platform} />
                <Status>{s.status}</Status>
              </div>
              <h3>{s.title}</h3>
              <p>
                {s.date} · {s.time}
              </p>
              <div className="session-meta-grid">
                <span>
                  <small>Class code</small>
                  <b>{s.code}</b>
                </span>
                <span>
                  <small>Candidates</small>
                  <b>{s.candidates}</b>
                </span>
                <span>
                  <small>Lead</small>
                  <b>{s.lead}</b>
                </span>
                <span>
                  <small>Risk level</small>
                  <b>{s.risk}</b>
                </span>
              </div>
              <div className="session-card-actions">
                <Button small icon="eye" onClick={() => cb.openModal("session-detail", s)}>
                  Details
                </Button>
                <Button small icon="external" variant="primary" onClick={() => cb.openClass(s, "proctor")}>
                  Open Class
                </Button>
              </div>
            </article>
          ))}
        </div>
      </Card>
    </>
  );
}

export function LabsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const labSessions = APP_DATA.sessions.filter((s: Session) => s.platform === "Chatoner AI Labs");
  const destinations = [
    {
      id: "dashboard",
      title: "Live Control Room",
      description: "Monitor assigned practical assessment sessions and human-attention signals.",
      icon: "monitor",
    },
    {
      id: "sessions",
      title: "Scheduled Sessions",
      description: "Open practical assessment assignments, session codes, timing and proctor scope.",
      icon: "calendar",
    },
    {
      id: "exam-room",
      title: "Exam Room",
      description: "Enter the selected Labs-owned practical assessment room with verified assignment scope.",
      icon: "shield",
    },
    {
      id: "incidents",
      title: "Incidents & Reviews",
      description: "Open factual runtime incidents and human-review records under Labs custody.",
      icon: "alert",
    },
    {
      id: "evidence",
      title: "Evidence Records",
      description: "Review permitted evidence metadata, integrity hashes and custody references.",
      icon: "database",
    },
    {
      id: "reports",
      title: "Proctoring Reports",
      description: "Open Labs-owned practical-assessment operations and quality reports.",
      icon: "pulse",
    },
    {
      id: "help",
      title: "Proctor Help",
      description: "Open Labs-specific role guidance, runtime support and emergency procedures.",
      icon: "help",
    },
  ];
  const active = labSessions[0];
  return (
    <>
      <PageHero
        kicker="Independent Practical Assessment Platform"
        title={route.title}
        description={route.description}
      >
        <Button icon="external" variant="primary" onClick={() => active && cb.openLab(active, "dashboard")}>
          Open Labs Proctor Workspace
        </Button>
        <Button icon="shield" onClick={() => cb.navigate("labs", "labs-data-boundaries")}>
          Data boundaries
        </Button>
      </PageHero>
      <div className="platform-hero">
        <div className="platform-hero-top">
          <span className="platform-logo">
            <Icon name="flask" size={24} />
          </span>
          <div>
            <h3>Chatoner AI Labs · labs.chatoner.com</h3>
            <p>
              Labs independently owns practical runtimes, exercises, prompts, working files, compute, tool
              access, runtime safety, practical incidents and Lab-native evidence. Academy coordinates
              assignment and will display purpose-limited synchronized status after verification.
            </p>
          </div>
        </div>
        <div className="connection-line">
          <i />
          Labs exam connection unavailable · assignment and entitlement verification pending
        </div>
      </div>
      <div className="metrics-grid">
        <Metric icon="flask" value="4" label="Assigned practical sessions" change="2 active or preflight" />
        <Metric
          icon="users"
          value="59"
          label="Practical candidates"
          change="54 readiness-complete"
          color="rgba(26,211,123,.15)"
          text="#72e5ad"
        />
        <Metric
          icon="alert"
          value="5"
          label="Runtime attention signals"
          change="No automated findings"
          color="rgba(247,127,0,.17)"
          text="#ffb45e"
        />
        <Metric
          icon="database"
          value="17"
          label="Returned evidence refs"
          change="Metadata only"
          color="rgba(47,214,244,.14)"
          text="#75e8fa"
        />
      </div>
      <Card
        title="Exact Labs destinations"
        subtitle="Academy does not reproduce these workspaces. A verified role- and session-scoped handoff is required before any Labs destination can open."
      >
        <div className="platform-grid">
          {destinations.map(d => (
            <article className="record-card" key={d.id}>
              <div className="record-head">
                <span className="list-icon">
                  <Icon name={d.icon} />
                </span>
                <span className="platform-chip labs">Labs</span>
              </div>
              <h3>{d.title}</h3>
              <p>{d.description}</p>
              <div className="record-actions">
                <Button
                  small
                  icon="external"
                  variant="primary"
                  onClick={() => active && cb.openLab(active, d.id)}
                >
                  Open {d.title}
                </Button>
              </div>
            </article>
          ))}
        </div>
      </Card>
      <Card
        title="Assigned practical assessment sessions"
        subtitle="Each card opens the intended Lab environment rather than a generic Labs home."
      >
        <div className="session-card-grid">
          {labSessions.map((s: Session, i: number) => (
            <article className="session-card" key={s.id}>
              <div className="session-card-top">
                <PlatformChip platform={s.platform} />
                <Status>{s.status}</Status>
              </div>
              <h3>{s.title}</h3>
              <p>
                {s.program} · {s.date} · {s.time}
              </p>
              <div className="session-meta-grid">
                <span>
                  <small>Lab environment</small>
                  <b>{["agentic-ai", "data-predictive-ai", "ai-app-builder", "conversational-ai"][i % 4]}</b>
                </span>
                <span>
                  <small>Candidates</small>
                  <b>
                    {s.candidates} · {s.ready} ready
                  </b>
                </span>
                <span>
                  <small>Proctor role</small>
                  <b>{s.role}</b>
                </span>
                <span>
                  <small>Runtime risk</small>
                  <b>{s.risk}</b>
                </span>
              </div>
              <Progress value={Math.round((s.ready / s.candidates) * 100)} green />
              <div className="session-card-actions">
                <Button small icon="eye" onClick={() => cb.openModal("session-detail", s)}>
                  Synced summary
                </Button>
                <Button small icon="external" variant="primary" onClick={() => cb.openLab(s, "exam-room")}>
                  Open intended Lab
                </Button>
              </div>
            </article>
          ))}
        </div>
      </Card>
      <div className="boundary-grid">
        <div className="boundary-card allow">
          <h3>
            <Icon name="check" />
            Academy proctor can receive
          </h3>
          <ul>
            {[
              "Assigned session, candidate roster and readiness status.",
              "Aggregate runtime state and human-attention signal category.",
              "Safety-stop state and permitted incident reference.",
              "Signed, consented evidence metadata returned by Labs.",
            ].map(v => (
              <li key={v}>
                <Icon name="check" size={12} />
                {v}
              </li>
            ))}
          </ul>
        </div>
        <div className="boundary-card deny">
          <h3>
            <Icon name="lock" />
            Academy proctor cannot automatically receive
          </h3>
          <ul>
            {[
              "Private prompts, working files or detailed outputs.",
              "Unrelated Lab histories, instructor notes or learner conversations.",
              "Broader compute or tool permissions than the assigned session.",
              "A misconduct conclusion generated from runtime signals.",
            ].map(v => (
              <li key={v}>
                <Icon name="close" size={12} />
                {v}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

export function CommunicationsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState("Candidate help queue");
  const [threads, setThreads] = useState([
    {
      id: 1,
      from: "Sarah Achieng",
      channel: "Candidate help queue",
      time: "14:41",
      subject: "Connection recovered",
      text: "I am back in the examination and can see my saved work.",
    },
    {
      id: 2,
      from: "Lydia Atim",
      channel: "Accessibility Office",
      time: "14:24",
      subject: "Alternative panel ready",
      text: "The authorized keyboard-only panel is available. Apply the approved timer pause.",
    },
    {
      id: 3,
      from: "Peter Ouma",
      channel: "Technical support",
      time: "14:18",
      subject: "Submission state preserved",
      text: "No answer data was lost during the reconnect.",
    },
    {
      id: 4,
      from: "Grace Namusoke",
      channel: "Integrity review",
      time: "14:09",
      subject: "Factual note requested",
      text: "Please add the observable event sequence and avoid an outcome conclusion.",
    },
  ]);
  const send = () => {
    if (!message.trim()) return;
    setThreads(v => [
      {
        id: Date.now(),
        from: "Amina Mutesi",
        channel,
        time: nowTime(),
        subject: "New secure message",
        text: message.trim(),
      },
      ...v,
    ]);
    setMessage("");
    cb.toast("Secure message sent", `Sent to ${channel}.`, "good");
  };
  return (
    <>
      <PageHero kicker="Session-Scoped Communication" title={route.title} description={route.description}>
        <Button icon="send" variant="primary" onClick={send}>
          Send message
        </Button>
        <Button icon="message" onClick={() => cb.openModal("broadcast")}>
          Broadcast
        </Button>
      </PageHero>
      <div className="dashboard-grid">
        <Card
          title="Secure inbox"
          subtitle="Candidate support, technical, accessibility, instructor, safeguarding and integrity-review channels are separated by purpose."
        >
          <div className="filters">
            <select
              className="select compact-select"
              value={channel}
              onChange={e => setChannel(e.target.value)}
            >
              <option>Candidate help queue</option>
              <option>Technical support</option>
              <option>Accessibility Office</option>
              <option>Instructor & Assessor</option>
              <option>Integrity review</option>
              <option>Safeguarding</option>
            </select>
          </div>
          <div className="message-thread">
            {threads.map(t => (
              <div className="message-bubble" key={t.id}>
                <span className="avatar">{initials(t.from)}</span>
                <div className="message-body">
                  <div className="message-head">
                    <b>
                      {t.from} · {t.channel}
                    </b>
                    <span>{t.time}</span>
                  </div>
                  <p>
                    <b style={{ fontSize: 14 }}>{t.subject}</b>
                    <br />
                    {t.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="composer">
            <input
              className="input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") send();
              }}
              placeholder={`Write to ${channel}…`}
            />
            <Button icon="send" variant="primary" onClick={send}>
              Send
            </Button>
          </div>
        </Card>
        <div>
          <Card title="Open help requests">
            <div className="list">
              {APP_DATA.candidates
                .filter((c: Candidate) => c.status !== "Normal")
                .slice(0, 7)
                .map((c: Candidate) => (
                  <button
                    className="list-row"
                    key={c.id}
                    onClick={() => cb.openModal("candidate-detail", c)}
                    style={{ width: "100%", textAlign: "left", color: "inherit" }}
                  >
                    <span className="avatar">{initials(c.name)}</span>
                    <span className="list-copy">
                      <b>{c.name}</b>
                      <span>
                        {c.status} · {c.lastEvent}
                      </span>
                    </span>
                    <Status>{c.status}</Status>
                  </button>
                ))}
            </div>
          </Card>
          <Card title="Emergency contacts">
            <div className="action-grid">
              <Button
                small
                icon="pulse"
                onClick={() => cb.openModal("candidate-message", { name: "Technical lead" })}
              >
                Technical
              </Button>
              <Button
                small
                icon="accessibility"
                onClick={() => cb.openModal("candidate-message", { name: "Accessibility" })}
              >
                Accessibility
              </Button>
              <Button
                small
                icon="shield"
                onClick={() => cb.openModal("candidate-message", { name: "Safeguarding" })}
              >
                Safeguarding
              </Button>
              <Button small icon="alert" variant="danger" onClick={() => cb.openModal("emergency")}>
                Emergency
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export function ReportsPage({ cb, route }: { cb: AppCallbacks; route: RouteDef }) {
  const bars = [92, 89, 94, 87, 91, 96, 93, 95, 90, 97, 94, 96];
  return (
    <>
      <PageHero
        kicker="Quality, Reliability & Accountability"
        title={route.title}
        description={route.description}
      >
        <Button icon="plus" variant="primary" onClick={() => cb.openModal("export", { mode: "builder" })}>
          Build report
        </Button>
        <Button icon="download" onClick={() => cb.openModal("export")}>
          Export
        </Button>
      </PageHero>
      <div className="metrics-grid">
        <Metric icon="check" value="94.1%" label="Readiness before start" change="+2.4 points" />
        <Metric
          icon="pulse"
          value="99.3%"
          label="Session continuity"
          change="Across all platforms"
          color="rgba(47,214,244,.15)"
          text="#76e8f9"
        />
        <Metric
          icon="history"
          value="11m"
          label="Median signal response"
          change="Within target"
          color="rgba(26,211,123,.15)"
          text="#72e5ad"
        />
        <Metric
          icon="accessibility"
          value="100%"
          label="Approved adjustments delivered"
          change="8 of 8"
          color="rgba(247,127,0,.16)"
          text="#ffb45d"
        />
      </div>
      <div className="dashboard-grid">
        <Card
          title="Session quality trend"
          subtitle="Twelve session-level operational quality scores. This is not a candidate-integrity score."
        >
          <div className="chart-bars" style={{ height: 210 }}>
            {bars.map((v, i) => (
              <div className="chart-bar-wrap" key={i}>
                <div className="chart-bar" data-value={`${v}%`} style={{ height: `${v}%` }} />
                <div className="chart-label">S{i + 1}</div>
              </div>
            ))}
          </div>
          <div className="triple" style={{ marginTop: 12 }}>
            {[
              ["Documentation completeness", 96, "clipboard"],
              ["Technical continuity", 93, "pulse"],
              ["Candidate support response", 91, "message"],
            ].map(([label, value, icon]) => (
              <div className="meta-item" key={String(label)}>
                <span>{label}</span>
                <b style={{ fontSize: 16, margin: "7px 0" }}>{value}%</b>
                <Progress value={Number(value)} green />
                <small>
                  <Icon name={String(icon)} size={12} /> Operational measure
                </small>
              </div>
            ))}
          </div>
        </Card>
        <Card
          title="Quality actions"
          subtitle="Named improvements without ranking candidates or using automated suspicion scores."
        >
          <div className="checklist">
            {[
              [
                "accessibility",
                "Accessibility rehearsal",
                "Run keyboard and caption checks before the next oral assessment.",
              ],
              ["pulse", "Connection fallback", "Add one more regional failover test before Saturday."],
              [
                "users",
                "Relief coverage",
                "Confirm a second relief proctor for the 38-candidate practical room.",
              ],
              [
                "document",
                "Report quality",
                "Complete two factual-note calibrations in the proctor practice simulation.",
              ],
            ].map(([icon, title, text]) => (
              <div className="check-row" key={title}>
                <span>
                  <Icon name={icon} />
                </span>
                <span className="check-row-copy">
                  <b>{title}</b>
                  <small>{text}</small>
                </span>
                <Button small onClick={() => cb.toast("Action assigned", title, "good")}>
                  Assign
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card
        title="Available reports"
        subtitle="Every export records purpose, recipient, scope, privacy threshold and retention."
      >
        <div className="report-grid">
          {[
            [
              "Session operations report",
              "Attendance, readiness, timing, technical continuity, support and handover.",
              "calendar",
            ],
            [
              "Incident process report",
              "Factual incident categories, response, escalation and resolution—not misconduct rates.",
              "alert",
            ],
            [
              "Technical reliability report",
              "Platform, device, network, reconnect and submission continuity.",
              "pulse",
            ],
            [
              "Accommodation delivery report",
              "Approved adjustment readiness and delivery without protected diagnoses.",
              "accessibility",
            ],
            [
              "Proctor quality review",
              "Training, factual-note quality, response targets and review feedback.",
              "star",
            ],
            [
              "Evidence custody report",
              "Evidence source, hash, access, export and retention state.",
              "database",
            ],
          ].map(([title, text, icon]) => (
            <article className="record-card" key={title}>
              <div className="record-head">
                <span className="list-icon">
                  <Icon name={icon} />
                </span>
                <Status>Available</Status>
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
              <div className="record-actions">
                <Button small icon="eye" onClick={() => cb.openModal("export", { title, preview: true })}>
                  Preview
                </Button>
                <Button
                  small
                  icon="download"
                  variant="primary"
                  onClick={() => cb.openModal("export", { title })}
                >
                  Prepare
                </Button>
              </div>
            </article>
          ))}
        </div>
      </Card>
    </>
  );
}

export function GovernancePage({
  cb,
  route,
  prefs,
  setPrefs,
}: {
  cb: AppCallbacks;
  route: RouteDef;
  prefs: Preferences;
  setPrefs: React.Dispatch<React.SetStateAction<Preferences>>;
}) {
  const account = useIdentity();
  const toggle = (key: keyof Preferences) => setPrefs(v => ({ ...v, [key]: !v[key] }));
  if (route.id === "language-accessibility")
    return (
      <>
        <PageHero kicker="Personal Workspace Preferences" title={route.title} description={route.description}>
          <Button
            icon="check"
            variant="primary"
            onClick={() =>
              cb.toast(
                "Preferences saved",
                "Language and accessibility preferences were saved for this browser demonstration.",
                "good",
              )
            }
          >
            Save preferences
          </Button>
        </PageHero>
        <div className="generic-board">
          <Card title="Language and translation">
            <div className="form-grid">
              <label className="field">
                <span>Primary interface language</span>
                <select
                  className="select"
                  value={prefs.primaryLanguage}
                  onChange={e => setPrefs(v => ({ ...v, primaryLanguage: e.target.value }))}
                >
                  {LANGUAGE_NAMES.map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Secondary translation</span>
                <select
                  className="select"
                  value={prefs.secondaryLanguage}
                  onChange={e => setPrefs(v => ({ ...v, secondaryLanguage: e.target.value }))}
                >
                  {[...LANGUAGE_NAMES, "None"].map(v => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="switch-list">
              <div className="switch-row">
                <span>
                  <b>Show translated messages</b>
                  <small>Preserve the original message and show an approved translation beside it.</small>
                </span>
                <Toggle
                  value={prefs.translations}
                  onChange={() => toggle("translations")}
                  label="Show translated messages"
                />
              </div>
              <div className="switch-row">
                <span>
                  <b>Class captions by default</b>
                  <small>Carry the caption preference into authorized Chatoner Class handoffs.</small>
                </span>
                <Toggle value={prefs.captions} onChange={() => toggle("captions")} label="Class captions" />
              </div>
            </div>
          </Card>
          <Card title="Accessibility and display">
            <div className="switch-list">
              <div className="switch-row">
                <span>
                  <b>High-contrast controls</b>
                  <small>Increase borders, focus and status distinction.</small>
                </span>
                <Toggle
                  value={prefs.highContrast}
                  onChange={() => toggle("highContrast")}
                  label="High contrast"
                />
              </div>
              <div className="switch-row">
                <span>
                  <b>Reduce non-essential motion</b>
                  <small>Keep state changes while reducing decorative movement.</small>
                </span>
                <Toggle
                  value={prefs.reducedMotion}
                  onChange={() => toggle("reducedMotion")}
                  label="Reduce motion"
                />
              </div>
              <div className="switch-row">
                <span>
                  <b>Compact information density</b>
                  <small>Show more operational rows while preserving readable labels.</small>
                </span>
                <Toggle value={prefs.compact} onChange={() => toggle("compact")} label="Compact layout" />
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  if (
    route.id === "notifications" ||
    route.id === "security-sessions" ||
    route.id === "connected-platform-settings"
  )
    return (
      <>
        <PageHero kicker="Proctor Workspace Settings" title={route.title} description={route.description}>
          <Button
            icon="check"
            variant="primary"
            onClick={() =>
              cb.toast("Settings saved", "The selected proctor workspace settings were saved.", "good")
            }
          >
            Save settings
          </Button>
        </PageHero>
        <div className="generic-board">
          <Card title="Operational notifications">
            <div className="switch-list">
              <div className="switch-row">
                <span>
                  <b>Signal sounds</b>
                  <small>Play a short non-alarming cue for new human-attention signals.</small>
                </span>
                <Toggle
                  value={prefs.signalSounds}
                  onChange={() => toggle("signalSounds")}
                  label="Signal sounds"
                />
              </div>
              <div className="switch-row">
                <span>
                  <b>Desktop notifications</b>
                  <small>Show assigned-session updates while the browser is in the background.</small>
                </span>
                <Toggle
                  value={prefs.desktopNotifications}
                  onChange={() => toggle("desktopNotifications")}
                  label="Desktop notifications"
                />
              </div>
              <div className="switch-row">
                <span>
                  <b>Auto-open urgent support</b>
                  <small>Open verified accessibility, safeguarding and emergency help requests.</small>
                </span>
                <Toggle
                  value={prefs.autoOpenUrgent}
                  onChange={() => toggle("autoOpenUrgent")}
                  label="Auto-open urgent support"
                />
              </div>
            </div>
          </Card>
          <Card title="Security and connected platforms">
            <div className="checklist">
              {[
                [
                  "shield",
                  "Multi-factor authentication",
                  account.security
                    ? account.security.mfaEnabled
                      ? "Authenticator enabled"
                      : "Authenticator not enabled"
                    : account.error || "Loading account security",
                ],
                [
                  "video",
                  "Chatoner Class",
                  "Connected · role-scoped one-time handoff required in production.",
                ],
                [
                  "flask",
                  "Chatoner AI Labs",
                  "Connected · intended Lab and assignment independently verified.",
                ],
                [
                  "lock",
                  "Active sessions",
                  account.security ? String(account.security.sessions.length) : "Loading",
                ],
                ["history", "Assigned roles", account.session?.roles.join(", ") || "No assigned roles"],
              ].map(([icon, title, text]) => (
                <div className="check-row" key={title}>
                  <span>
                    <Icon name={icon} />
                  </span>
                  <span className="check-row-copy">
                    <b>{title}</b>
                    <small>{text}</small>
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </>
    );
  if (route.id === "authority-boundaries" || route.id === "privacy-data-minimization")
    return (
      <>
        <PageHero kicker="Human Authority & Privacy" title={route.title} description={route.description}>
          <Button icon="document" onClick={() => cb.openModal("policy-confirmation")}>
            Acknowledge policy
          </Button>
        </PageHero>
        <div className="boundary-grid">
          <div className="boundary-card allow">
            <h3>
              <Icon name="check" />
              Exam Proctor may
            </h3>
            <ul>
              {[
                "Observe only assigned assessment sessions and session-scoped candidates.",
                "Manually verify admission using approved methods and alternatives.",
                "Provide neutral procedural, technical, accessibility and wellbeing support.",
                "Apply pre-approved breaks, timing and delivery accommodations.",
                "Record observable facts, actions, evidence references and handovers.",
                "Escalate to authorized integrity, safeguarding, technical or accessibility reviewers.",
                "Use an emergency pause where the documented authority permits it.",
              ].map(v => (
                <li key={v}>
                  <Icon name="check" size={13} />
                  {v}
                </li>
              ))}
            </ul>
          </div>
          <div className="boundary-card deny">
            <h3>
              <Icon name="lock" />
              Exam Proctor may not
            </h3>
            <ul>
              {[
                "Write or change examination content, explain answers or coach candidates.",
                "Grade, moderate marks, decide misconduct, guilt, sanctions or credential outcomes.",
                "Treat an automated signal as proof or exclude a candidate without human authority.",
                "Broaden surveillance beyond the published assessment policy.",
                "Use facial recognition or biometric matching as an automatic admission decision.",
                "Access unrelated learner, Instructor, Class or Labs information.",
                "Export or retain evidence outside its authorized purpose and retention rule.",
              ].map(v => (
                <li key={v}>
                  <Icon name="close" size={13} />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </>
    );
  return (
    <>
      <PageHero kicker="Policies, Training & Readiness" title={route.title} description={route.description}>
        <Button icon="book" variant="primary" onClick={() => cb.openModal("policy-confirmation")}>
          Open current policy
        </Button>
        <Button
          icon="play"
          onClick={() =>
            cb.toast(
              "Practice simulation opened",
              "A no-stakes proctor scenario is ready in the training workspace.",
              "good",
            )
          }
        >
          Practice simulation
        </Button>
      </PageHero>
      <div className="metrics-grid">
        <Metric icon="check" value="Current" label="Proctor certification" change="Valid to 31 Dec 2026" />
        <Metric
          icon="star"
          value="4.8/5"
          label="Factual-note quality"
          change="Last 12 reviews"
          color="rgba(47,214,244,.14)"
          text="#76e8f9"
        />
        <Metric
          icon="shield"
          value="100%"
          label="Policy acknowledgements"
          change="All current"
          color="rgba(26,211,123,.15)"
          text="#72e5ad"
        />
        <Metric
          icon="clock"
          value="2"
          label="Training actions"
          change="Due this month"
          color="rgba(247,127,0,.16)"
          text="#ffb45d"
        />
      </div>
      <div className="policy-grid">
        {[
          [
            "Proctoring policy",
            "Scope, conduct, procedural support, evidence and escalation.",
            "shield",
            "Current",
          ],
          [
            "Privacy and data minimization",
            "Need-to-know information, no unnecessary surveillance and purpose-limited retention.",
            "lock",
            "Current",
          ],
          [
            "Safeguarding and wellbeing",
            "Protected routing, emergency action and minimum necessary recording.",
            "users",
            "Current",
          ],
          [
            "Accessibility delivery",
            "Approved accommodations, assistive technology and barrier escalation.",
            "accessibility",
            "Current",
          ],
          [
            "Factual incident writing",
            "Separate observations, source, action and evidence from conclusions.",
            "document",
            "Practice due",
          ],
          [
            "Class and Labs boundaries",
            "Independent platform ownership, signed handoffs and scoped synchronization.",
            "link",
            "Current",
          ],
        ].map(([title, text, icon, status]) => (
          <article className="record-card" key={title}>
            <div className="record-head">
              <span className="list-icon">
                <Icon name={icon} />
              </span>
              <Status>{status}</Status>
            </div>
            <h3>{title}</h3>
            <p>{text}</p>
            <div className="record-actions">
              <Button small icon="eye" onClick={() => cb.openModal("policy-confirmation", { title, text })}>
                Open
              </Button>
              <Button
                small
                icon="check"
                variant="primary"
                onClick={() => cb.toast("Policy acknowledged", title, "good")}
              >
                Acknowledge
              </Button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
